import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HardHat, ArrowLeft, Download, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { exportCSV } from "../lib/csvExport";
import { psaCatRank } from "../lib/psaConstants";
import HistoryDialog from "./inventar/HistoryDialog";
import PSACategoryIcon from "../components/PSACategoryIcon";
import ActivityFeed from "../components/ActivityFeed";

const CAT_LABEL = {
  masca: "Vollmaske (mască)", furtun: "Furtun", curea: "Curea",
  cizme: "Cizme", tricou: "Tricou", hanorac: "Hanorac", geaca: "Geacă",
  casca: "Cască",
  bocanci: "Bocanci", pantaloni: "Pantaloni", altele: "Altele",
};

export default function RaportPSA() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState({ rows: [], grand_total_chf: 0, persons_count: 0, items_count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  // Modal pentru detaliile fizice pe categorie + persoană
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailContext, setDetailContext] = useState({ personId: null, personName: "", category: null });
  const [detailItems, setDetailItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  // Pentru istoricul unui singur item (sub-modal deschis peste detail dialog)
  const [historyItem, setHistoryItem] = useState(null);

  const openCategoryDetail = async (personId, personName, category) => {
    setDetailContext({ personId, personName, category });
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const r = await api.get(`/persons/${personId}/psa`);
      const all = r.data || [];
      const filtered = category ? all.filter((it) => (it.psa_category || "altele") === category) : all;
      setDetailItems(filtered);
    } catch {
      setDetailItems([]);
    } finally { setDetailLoading(false); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const r = await api.get("/admin/psa-cost-report");
        setData(r.data || { rows: [], grand_total_chf: 0, persons_count: 0, items_count: 0 });
      } finally { setLoading(false); }
    })();
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.rows || []).filter((r) => {
      if (!showInactive && !r.active) return false;
      if (!q) return true;
      return (r.name || "").toLowerCase().includes(q);
    });
  }, [data.rows, search, showInactive]);

  // Câți angajați inactivi/concediați au PSA dar sunt ascunși de filtrul curent?
  const hiddenInactiveCount = useMemo(() => {
    if (showInactive) return 0;
    return (data.rows || []).filter((r) => !r.active).length;
  }, [data.rows, showInactive]);

  if (!isAdmin) {
    return <div className="card-flat p-6 text-sm text-zinc-600">Acces restricționat — doar administratori.</div>;
  }

  return (
    <div className="space-y-5" data-testid="page-raport-psa">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/" className="text-xs text-zinc-500 hover:text-zinc-700 inline-flex items-center gap-1 mb-1">
            <ArrowLeft size={12}/> Dashboard
          </Link>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
            <HardHat size={28} className="text-cyan-700"/> Istoric PSA
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Istoric și cost PSA per angajat — click pe o categorie pentru detalii, apoi pe un item pentru istoricul lui complet în inventar.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportCSV(filtered, [
            { key: "name", label: "Angajat" },
            { key: (r) => (r.active ? "Activ" : "Inactiv"), label: "Status" },
            { key: "items_count", label: "Nr. items PSA" },
            { key: (r) => r.total_chf.toFixed(2), label: "Cost total CHF" },
            { key: "items_without_price", label: "Items fără preț" },
          ], "raport-psa")}
          data-testid="raport-psa-export"
        >
          <Download size={14} className="mr-1"/> Export CSV
        </Button>
      </div>

      {/* Sumare */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="card-flat p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Cost total</div>
          <div className="font-display text-3xl font-extrabold text-emerald-700 mt-1" data-testid="raport-grand-total">
            {(data.grand_total_chf || 0).toFixed(2)} <span className="text-base font-normal text-zinc-500">CHF</span>
          </div>
        </div>
        <div className="card-flat p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Angajați cu PSA</div>
          <div className="font-display text-3xl font-extrabold mt-1">{data.persons_count}</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Items predate total</div>
          <div className="font-display text-3xl font-extrabold mt-1">{data.items_count}</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Cost mediu/angajat</div>
          <div className="font-display text-3xl font-extrabold mt-1 text-cyan-700">
            {data.persons_count > 0 ? (data.grand_total_chf / data.persons_count).toFixed(2) : "0.00"}
            <span className="text-base font-normal text-zinc-500"> CHF</span>
          </div>
        </div>
      </div>

      {/* Filtre */}
      <div className="card-flat p-3 flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Caută angajat..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-[300px]"
          data-testid="raport-psa-search"
        />
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="raport-psa-show-inactive"
          />
          <span>Arată inactivi/concediați</span>
        </label>
        {hiddenInactiveCount > 0 && (
          <button
            type="button"
            onClick={() => setShowInactive(true)}
            className="text-[11px] px-2 py-1 rounded bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100 font-semibold"
            data-testid="raport-psa-hidden-inactive-hint"
            title="Click pentru a-i afișa"
          >
            ⚠ {hiddenInactiveCount} {hiddenInactiveCount === 1 ? "angajat inactiv/concediat are" : "angajați inactivi/concediați au"} încă PSA · arată-i
          </button>
        )}
        <div className="ml-auto text-xs text-zinc-500">{filtered.length} angajat(i)</div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="card-flat p-6 text-sm text-zinc-500 italic">Se încarcă...</div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-8 text-center text-sm text-zinc-500 italic" data-testid="raport-psa-empty">
          Niciun angajat cu PSA asignat încă.
        </div>
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-600">
              <tr>
                <th className="text-left p-3">Angajat</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">PSA per categorie</th>
                <th className="text-right p-3">Items</th>
                <th className="text-right p-3">Cost CHF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((r) => (
                <tr key={r.person_id} className="hover:bg-zinc-50/60" data-testid={`raport-psa-row-${r.person_id}`}>
                  <td className="p-3">
                    <Link to={`/personal/${r.person_id}`} className="font-semibold hover:underline text-cyan-800">
                      {r.name}
                    </Link>
                  </td>
                  <td className="p-3">
                    {r.active ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">Activ</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-zinc-200 text-zinc-700 font-semibold">Inactiv</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(r.by_category || {})
                        .sort(([a], [b]) => psaCatRank(a) - psaCatRank(b))
                        .map(([cat, n]) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => openCategoryDetail(r.person_id, r.name, cat)}
                          className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-cyan-50 border border-cyan-200 text-cyan-900 hover:bg-cyan-100 hover:border-cyan-400 cursor-pointer transition-colors"
                          title={`Vezi detalii pentru ${CAT_LABEL[cat] || cat}`}
                          data-testid={`raport-psa-cat-badge-${r.person_id}-${cat}`}
                        >
                          <PSACategoryIcon cat={cat} size={14} className="text-cyan-800"/>
                          {n}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono">{r.items_count}</td>
                  <td className="p-3 text-right font-bold text-emerald-700">
                    {r.total_chf.toFixed(2)}
                    {r.items_without_price > 0 && (
                      <div className="text-[10px] text-amber-700 font-normal">⚠ {r.items_without_price} fără preț</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-emerald-50 font-bold">
              <tr>
                <td colSpan={3} className="p-3 text-right">TOTAL</td>
                <td className="p-3 text-right font-mono">{filtered.reduce((s, r) => s + r.items_count, 0)}</td>
                <td className="p-3 text-right text-emerald-800" data-testid="raport-psa-filtered-total">
                  {filtered.reduce((s, r) => s + r.total_chf, 0).toFixed(2)} CHF
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Modal cu detaliile fizice ale items dintr-o categorie pentru o persoană */}
      <Dialog open={detailOpen} onOpenChange={(v) => !v && setDetailOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="raport-psa-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PSACategoryIcon cat={detailContext.category} size={36} className="text-cyan-700"/>
              <div>
                <div>{CAT_LABEL[detailContext.category] || detailContext.category} — {detailContext.personName}</div>
                <div className="text-xs font-normal text-zinc-500">{detailItems.length} item{detailItems.length === 1 ? "" : "e"} alocat{detailItems.length === 1 ? "" : "e"}</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="text-sm text-zinc-500 italic text-center py-8">Se încarcă...</div>
          ) : detailItems.length === 0 ? (
            <div className="text-sm text-zinc-500 italic text-center py-8" data-testid="raport-psa-detail-empty">
              Niciun item alocat în această categorie.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-600">
                  <tr>
                    <th className="text-left p-2">#</th>
                    <th className="text-left p-2">Denumire</th>
                    <th className="text-left p-2">Nr. serie</th>
                    <th className="text-center p-2">Cantitate</th>
                    <th className="text-center p-2">Măsură</th>
                    <th className="text-left p-2">Model</th>
                    <th className="text-right p-2">Preț CHF</th>
                    <th className="text-left p-2">Predat la</th>
                    <th className="text-center p-2">Istoric</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {detailItems.map((it, idx) => (
                    <tr
                      key={it.id}
                      onClick={() => setHistoryItem({ id: it.id, name: it.name || "PSA" })}
                      className="cursor-pointer hover:bg-cyan-50/60 transition-colors"
                      data-testid={`raport-psa-detail-row-${idx}`}
                      title="Click pentru istoricul complet al item-ului în inventar"
                    >
                      <td className="p-2 text-zinc-500 font-mono">{idx + 1}</td>
                      <td className="p-2 font-semibold">{it.name || "—"}</td>
                      <td className="p-2 font-mono text-xs">
                        {it.serial ? it.serial : <span className="italic text-zinc-400">fără serie</span>}
                      </td>
                      <td className="p-2 text-center font-mono">{it.quantity || 1}{it.unit ? " " + it.unit : ""}</td>
                      <td className="p-2 text-center">
                        {it.psa_size ? (
                          <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-bold text-xs">{it.psa_size}</span>
                        ) : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="p-2">
                        {it.supplier ? (
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200 text-xs">{it.supplier}</span>
                        ) : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="p-2 text-right font-bold text-emerald-700">
                        {it.price_chf != null ? (parseFloat(it.price_chf) * (parseFloat(it.quantity) || 1)).toFixed(2) : <span className="text-zinc-400 font-normal">—</span>}
                      </td>
                      <td className="p-2 text-[11px] text-zinc-600">
                        {it.assigned_at ? new Date(it.assigned_at).toLocaleDateString("ro-RO") : "—"}
                      </td>
                      <td className="p-2 text-center">
                        <span className="inline-flex items-center gap-1 text-[11px] text-cyan-700 hover:text-cyan-900 font-semibold underline-offset-2 hover:underline" data-testid={`raport-psa-item-history-${it.id}`}>
                          🕘 vezi
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-emerald-50 font-bold">
                  <tr>
                    <td colSpan={6} className="p-2 text-right">TOTAL</td>
                    <td className="p-2 text-right text-emerald-800">
                      {detailItems.reduce((s, it) => s + (it.price_chf != null ? parseFloat(it.price_chf) * (parseFloat(it.quantity) || 1) : 0), 0).toFixed(2)} CHF
                    </td>
                    <td/>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <DialogFooter>
            <Link
              to={`/personal/${detailContext.personId}`}
              className="text-xs text-cyan-700 hover:underline flex items-center gap-1"
            >
              <ArrowLeft size={12} className="rotate-180"/> Deschide fișa lui {detailContext.personName}
            </Link>
            <Button variant="outline" onClick={() => setDetailOpen(false)} data-testid="raport-psa-detail-close">
              <X size={14} className="mr-1"/> Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-modal: istoricul complet al unui item PSA — unde a fost, când, cine l-a mutat */}
      {historyItem && (
        <HistoryDialog item={historyItem} onClose={() => setHistoryItem(null)} />
      )}
      <div className="mt-8 card-flat p-6">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
          📱 Cronologie PSA
          <span className="text-xs text-zinc-500 font-normal">— cine a predat / preluat / returnat / schimbat mărimea</span>
        </h2>
        <ActivityFeed category="psa" pageSize={8} compact/>
      </div>
    </div>
  );
}
